/**
 * Full-screen dialog for adding or editing a customization rule.
 * Sentence-builder UI: reads as natural language.
 *
 *   If [field] [condition] [value], then [action] [target field]
 *
 * In add mode: form resets after success, dialog stays open.
 * In edit mode: dialog closes after save.
 *
 * Plan: 126-template-detail-page
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";
import type { TemplateField, TemplateRule } from "../api/customizationAdminTypes";
import {
  RULE_OPERATORS,
  RULE_ACTIONS,
  VALUE_LESS_OPERATORS,
  VALUE_ACTIONS,
} from "../api/customizationAdminTypes";
import { generateRuleSummary } from "../api/customizationAdminUtils";

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------

const i18n = {
  addRule: { en: "Add Rule", th: "เพิ่มกฎ" },
  editRule: { en: "Edit Rule", th: "แก้ไขกฎ" },
  addDesc: {
    en: "Build your rule by completing the sentence below.",
    th: "สร้างกฎโดยเติมประโยคด้านล่างให้สมบูรณ์",
  },
  editDesc: {
    en: "Update the rule by changing the sentence below.",
    th: "อัปเดตกฎโดยเปลี่ยนประโยคด้านล่าง",
  },
  if: { en: "If", th: "ถ้า" },
  then: { en: "then", th: "แล้ว" },
  selectField: { en: "select field", th: "เลือกฟิลด์" },
  enterValue: { en: "value", th: "ค่า" },
  cancel: { en: "Cancel", th: "ยกเลิก" },
  saveChanges: { en: "Save Changes", th: "บันทึกการเปลี่ยนแปลง" },
  preview: { en: "This rule will:", th: "กฎนี้จะ:" },
} as const;

// Operator labels in Thai
const operatorLabels: Record<string, { en: string; th: string }> = {
  equals: { en: "equals", th: "เท่ากับ" },
  not_equals: { en: "does not equal", th: "ไม่เท่ากับ" },
  is_set: { en: "is filled in", th: "มีการกรอก" },
  is_empty: { en: "is empty", th: "ว่างเปล่า" },
  contains: { en: "contains", th: "มีคำว่า" },
};

// Action labels in Thai
const actionLabels: Record<string, { en: string; th: string }> = {
  "Show Field": { en: "show", th: "แสดง" },
  "Hide Field": { en: "hide", th: "ซ่อน" },
  "Set Value": { en: "set value of", th: "ตั้งค่าให้" },
  "Force Value": { en: "force value of", th: "บังคับค่าให้" },
  "Set Required": { en: "make required", th: "บังคับกรอก" },
  "Clear Required": { en: "make optional", th: "ไม่บังคับกรอก" },
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RuleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editRule?: TemplateRule | null;
  fields: TemplateField[];
  onSave: (rule: TemplateRule) => void;
}

const EMPTY_RULE: Omit<TemplateRule, "name"> = {
  when_field: "",
  operator: "equals",
  has_value: "",
  then_action: "Show Field",
  target_field: "",
  target_value: "",
  rule_group: "A",
  priority: 10,
  summary: "",
};

// ---------------------------------------------------------------------------
// Inline select — styled to look like part of the sentence
// ---------------------------------------------------------------------------

function SentenceSelect({
  value,
  onValueChange,
  placeholder,
  options,
  className,
}: {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className={cn(
          "h-auto border-0 border-b-2 border-primary/30 rounded-none bg-transparent px-1 py-0.5",
          "text-base font-semibold text-primary shadow-none",
          "focus:ring-0 focus:border-primary",
          "hover:border-primary/60 transition-colors",
          "[&>svg]:ml-1 [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-primary/50",
          !value && "text-primary/40 font-normal italic",
          className,
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SentenceInput({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "h-auto border-0 border-b-2 border-primary/30 rounded-none bg-transparent px-1 py-0.5",
        "text-base font-semibold shadow-none",
        "focus-visible:ring-0 focus-visible:border-primary",
        "hover:border-primary/60 transition-colors",
        "placeholder:text-muted-foreground/40 placeholder:italic placeholder:font-normal",
        className,
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RuleSheet({ open, onOpenChange, editRule, fields, onSave }: RuleSheetProps) {
  const { language } = useLanguage();
  const t = (key: keyof typeof i18n) => i18n[key][language] || i18n[key].en;

  const isEdit = !!editRule;
  const [form, setForm] = useState(EMPTY_RULE);

  useEffect(() => {
    if (open) {
      if (editRule) {
        setForm({
          when_field: editRule.when_field,
          operator: editRule.operator,
          has_value: editRule.has_value,
          then_action: editRule.then_action,
          target_field: editRule.target_field,
          target_value: editRule.target_value,
          rule_group: editRule.rule_group || "A",
          priority: editRule.priority || 10,
          summary: editRule.summary || "",
        });
      } else {
        setForm(EMPTY_RULE);
      }
    }
  }, [open, editRule]);

  const fieldLabels = useMemo(
    () => fields.map((f) => f.label).filter(Boolean),
    [fields],
  );

  const targetFieldOptions = useMemo(
    () => fieldLabels.filter((l) => l !== form.when_field),
    [fieldLabels, form.when_field],
  );

  const showHasValue = !VALUE_LESS_OPERATORS.includes(form.operator as never);
  const showTargetValue = VALUE_ACTIONS.includes(form.then_action as never);

  // Smart value options — pull from the selected field's predefined options
  const getFieldMeta = useCallback(
    (fieldLabel: string) => {
      const field = fields.find((f) => f.label === fieldLabel);
      if (!field) return null;

      if (field.field_type === "Single Select" || field.field_type === "Multi Select") {
        const opts = (field.options || "").split("\n").filter(Boolean);
        return opts.length > 0 ? { type: "select" as const, options: opts } : null;
      }
      if (field.field_type === "Toggle") {
        return {
          type: "select" as const,
          options: language === "th" ? ["ใช่", "ไม่ใช่"] : ["Yes", "No"],
        };
      }
      return null; // Text Input, Text Area, File Upload → free text
    },
    [fields, language],
  );

  const whenFieldMeta = useMemo(() => getFieldMeta(form.when_field), [getFieldMeta, form.when_field]);
  const targetFieldMeta = useMemo(() => getFieldMeta(form.target_field), [getFieldMeta, form.target_field]);

  const preview = useMemo(() => generateRuleSummary(form), [form]);

  const updateField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const canSave = form.when_field && form.then_action && form.target_field;

  const handleSave = useCallback(() => {
    if (!canSave) return;
    const summary = generateRuleSummary(form);
    const rule: TemplateRule = {
      name: editRule?.name,
      ...form,
      summary,
    };
    onSave(rule);

    if (!isEdit) {
      setForm(EMPTY_RULE);
    }
  }, [form, canSave, isEdit, editRule, onSave]);

  // Build translated option arrays
  const fieldOptions = fieldLabels.map((l) => ({ value: l, label: l }));
  const targetOptions = targetFieldOptions.map((l) => ({ value: l, label: l }));
  const opOptions = RULE_OPERATORS.map((op) => ({
    value: op.value,
    label: operatorLabels[op.value]?.[language] || op.label,
  }));
  const actionOptions = RULE_ACTIONS.map((a) => ({
    value: a.value,
    label: actionLabels[a.value]?.[language] || a.label,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[90vw] max-h-[92vh] flex flex-col p-0 [&>button.absolute]:hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-8 pt-8 pb-2">
          <div>
            <DialogTitle className="text-xl font-semibold">
              {isEdit ? t("editRule") : t("addRule")}
            </DialogTitle>
            <DialogDescription className="mt-1">
              {isEdit ? t("editDesc") : t("addDesc")}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="shrink-0 ml-4 mt-0.5 rounded-full p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Sentence builder */}
        <div className="flex-1 min-h-0 overflow-y-auto px-8 py-6">
          <div className="rounded-md border bg-muted/30 p-8">
            {/* IF line */}
            <div className="flex flex-wrap items-end gap-x-2 gap-y-3 text-lg leading-relaxed">
              <span className="font-bold text-foreground text-xl">{t("if")}</span>
              <SentenceSelect
                value={form.when_field}
                onValueChange={(v) => updateField("when_field", v)}
                placeholder={t("selectField")}
                options={fieldOptions}
                className="min-w-[140px] max-w-[200px]"
              />
              <SentenceSelect
                value={form.operator}
                onValueChange={(v) => updateField("operator", v)}
                placeholder="condition"
                options={opOptions}
                className="min-w-[120px] max-w-[180px]"
              />
              {showHasValue && (
                whenFieldMeta?.type === "select" ? (
                  <SentenceSelect
                    value={form.has_value}
                    onValueChange={(v) => updateField("has_value", v)}
                    placeholder={t("enterValue")}
                    options={whenFieldMeta.options.map((o) => ({ value: o, label: o }))}
                    className="min-w-[100px] max-w-[200px]"
                  />
                ) : (
                  <SentenceInput
                    value={form.has_value}
                    onChange={(v) => updateField("has_value", v)}
                    placeholder={t("enterValue")}
                    className="min-w-[100px] max-w-[160px]"
                  />
                )
              )}
            </div>

            {/* Connector arrow */}
            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                ↓
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* THEN line */}
            <div className="flex flex-wrap items-end gap-x-2 gap-y-3 text-lg leading-relaxed">
              <span className="font-bold text-foreground text-xl">{t("then")}</span>
              <SentenceSelect
                value={form.then_action}
                onValueChange={(v) => updateField("then_action", v)}
                placeholder="action"
                options={actionOptions}
                className="min-w-[120px] max-w-[200px]"
              />
              <SentenceSelect
                value={form.target_field}
                onValueChange={(v) => {
                  updateField("target_field", v);
                  updateField("target_value", ""); // clear stale value when field changes
                }}
                placeholder={t("selectField")}
                options={targetOptions}
                className="min-w-[140px] max-w-[200px]"
              />
              {showTargetValue && (
                targetFieldMeta?.type === "select" ? (
                  <SentenceSelect
                    value={form.target_value}
                    onValueChange={(v) => updateField("target_value", v)}
                    placeholder={t("enterValue")}
                    options={targetFieldMeta.options.map((o) => ({ value: o, label: o }))}
                    className="min-w-[100px] max-w-[200px]"
                  />
                ) : (
                  <SentenceInput
                    value={form.target_value}
                    onChange={(v) => updateField("target_value", v)}
                    placeholder={t("enterValue")}
                    className="min-w-[100px] max-w-[160px]"
                  />
                )
              )}
            </div>
          </div>

          {/* Live preview */}
          {canSave && (
            <div className="mt-6 rounded bg-primary/5 border border-primary/10 p-4">
              <p className="text-xs font-medium text-muted-foreground mb-1">{t("preview")}</p>
              <p className="text-sm text-foreground">{preview}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t px-8 py-5">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isEdit ? t("saveChanges") : t("addRule")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
